import {Injectable} from '@angular/core';
import {findIndex, intersectionBy} from 'lodash';
import {BehaviorSubject, Observable} from 'rxjs';
import {IMetadataList} from 'src/app/editor-interface/models/metadata-list.interface';

import {summaryInfoObjectValue} from '../components/custom-metadata-view/dialogs/models/create-new-view.enum';
import {
  IColumn,
  IColumnField,
  ISelectedColumnsEmit,
  ISelectedMetadata
} from '../components/custom-metadata-view/dialogs/models/create-new-view.interface';
import {ICustomView, ICustomViewFilter} from '../models/custom-view.interface';

@Injectable()
export class CustomViewService {
  private refModelMetadata$: BehaviorSubject<IMetadataList> = new BehaviorSubject<IMetadataList>(
    {} as IMetadataList
  );

  private gridName$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private gridDescription$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private isPrivateGrid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private selectedMetadata$: BehaviorSubject<ISelectedMetadata> =
    new BehaviorSubject<ISelectedMetadata>({} as ISelectedMetadata);

  private refModelColumns$: BehaviorSubject<IColumnField[]> = new BehaviorSubject<IColumnField[]>(
    []
  );

  private selectedColumns$: BehaviorSubject<ISelectedColumnsEmit> =
    new BehaviorSubject<ISelectedColumnsEmit>({} as ISelectedColumnsEmit);

  private filters$: BehaviorSubject<ICustomViewFilter[]> = new BehaviorSubject<ICustomViewFilter[]>(
    []
  );

  private customViewList$: BehaviorSubject<ICustomView[]> = new BehaviorSubject<ICustomView[]>(
    null
  );
  private customViewListObservable$: Observable<ICustomView[]> =
    this.customViewList$.asObservable();

  private updateCustomViewList$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private updateCustomViewListObservable$: Observable<boolean> =
    this.updateCustomViewList$.asObservable();

  private filterOnlyColumns: BehaviorSubject<IColumnField[]> = new BehaviorSubject<IColumnField[]>(
    []
  );

  private selectedView$: BehaviorSubject<ICustomView | string> = new BehaviorSubject<
    ICustomView | string
  >({} as ICustomView);

  set selectedView(customView: ICustomView | string) {
    this.selectedView$.next(customView);
  }

  get selectedView(): ICustomView | string {
    return this.selectedView$.getValue();
  }

  subscribeOnSelectedView(): Observable<ICustomView | string> {
    return this.selectedView$.asObservable();
  }

  public storeRefModelMetadata(payload: IMetadataList): void {
    this.refModelMetadata$.next(payload);
  }

  public storeGridName(name: string): void {
    this.gridName$.next(name);
  }

  public storeGridDescription(description: string): void {
    this.gridDescription$.next(description);
  }

  public storeSelectedMetadata(payload: ISelectedMetadata): void {
    this.selectedMetadata$.next(payload);
  }

  public storeRefModelColumns(payload: IColumnField[]): void {
    this.refModelColumns$.next(payload);
  }

  public storeSelectedColumns(payload: ISelectedColumnsEmit): void {
    this.selectedColumns$.next(payload);
  }

  public storeFilters(filters: ICustomViewFilter[]): void {
    this.filters$.next(filters || []);
  }

  public retrieveFilters(): ICustomViewFilter[] {
    return this.filters$.getValue();
  }

  /**
   * Drop stored filters whose column is no longer among the selected columns.
   * Filter-only columns are not part of the displayed selection but remain valid
   * filter targets, so they are always kept.
   */
  public pruneStoredFiltersToSelectedColumns(): void {
    const filterOnlyValues = this.refModelColumns$
      .getValue()
      .filter(column => column.filterOnly)
      .map(column => column.value);
    const columnValues = new Set([
      ...(this.selectedColumns$.getValue().selectedColumns || []).map(
        column => column.field?.value
      ),
      ...filterOnlyValues
    ]);
    this.storeFilters(this.filters$.getValue().filter(filter => columnValues.has(filter.column)));
  }

  public storeCustomViewList(payload: ICustomView[]): void {
    this.customViewList$.next(payload);
  }

  public storeIsPrivateGrid(isPrivate: boolean): void {
    this.isPrivateGrid$.next(isPrivate);
  }

  public retrieveRefModelMetadata(): IMetadataList {
    return this.refModelMetadata$.getValue();
  }

  public retrieveGridName(): string {
    return this.gridName$.getValue();
  }

  public retrieveGridDescription(): string {
    return this.gridDescription$.getValue();
  }

  public retrieveSelectedMetadata(): ISelectedMetadata {
    return this.selectedMetadata$.getValue();
  }

  public retrieveRefModelColumns(): IColumnField[] {
    return this.refModelColumns$.getValue();
  }

  public retrieveSelectedColumns(): ISelectedColumnsEmit {
    return this.selectedColumns$.getValue();
  }

  public retrieveIsPrivateGrid(): boolean {
    return this.isPrivateGrid$.getValue();
  }

  public subscribeOnCustomViewList(): Observable<ICustomView[]> {
    return this.customViewListObservable$;
  }

  public addNewCustomView(payload: ICustomView): void {
    const customViewList = this.customViewList$.getValue();

    customViewList.push(payload);

    this.customViewList$.next(customViewList);
  }

  public updateCustomView(payload: ICustomView): void {
    const customViewList = this.customViewList$.getValue();

    const index = findIndex(customViewList, ({_id}) => payload._id === _id);

    customViewList[index] = payload;

    this.customViewList$.next(customViewList);
  }

  public removeCustomView(payload: ICustomView): void {
    const customViewList = this.customViewList$.getValue();

    const index = findIndex(customViewList, ({_id}) => payload._id === _id);

    const updated = [...customViewList.slice(0, index), ...customViewList.slice(index + 1)];

    this.customViewList$.next(updated);
  }

  public resetModal(): void {
    this.storeGridName('');
    this.storeGridDescription('');
    this.storeIsPrivateGrid(false);
    this.storeRefModelMetadata({} as IMetadataList);
    this.storeRefModelColumns([] as IColumnField[]);
    this.storeSelectedColumns({} as ISelectedColumnsEmit);
    this.storeSelectedMetadata({} as ISelectedMetadata);
    this.storeFilters([]);
    this.setFilterOnlyColumns([]);
  }

  /**
   * Collapse a dotted SIO filter into the persisted `{column, subcolumn}` form,
   * keeping every other filter field (secondValue, values, tags, list, ...).
   */
  public createFilterObjectForSio(filter: ICustomViewFilter): ICustomViewFilter {
    const {column, ...rest} = filter;

    return {
      ...rest,
      column: summaryInfoObjectValue,
      subcolumn: column.split('.')[1]
    };
  }

  public mapFiltersForSio(filters: ICustomViewFilter[]): ICustomViewFilter[] {
    return filters.map(filter =>
      filter.column.startsWith(`${summaryInfoObjectValue}.`)
        ? this.createFilterObjectForSio(filter)
        : filter
    );
  }

  /**
   * Expand persisted SIO filters (`{column: summaryInfoObjectValue, subcolumn}`)
   * into the dotted `summaryInfoObjectValue.<subcolumn>` column form the create/edit
   * forms, grid columns and filter pruning use. Inverse of `mapFiltersForSio`.
   */
  public parseSioFilters(filters: ICustomViewFilter[]): ICustomViewFilter[] {
    return filters.map(filter => {
      if (filter.column !== summaryInfoObjectValue) {
        return filter;
      }

      const {subcolumn, ...rest} = filter;

      return {...rest, column: `${filter.column}.${subcolumn}`};
    });
  }

  public flatSubcolumns(columns) {
    return columns.reduce((list, column) => {
      if (column.value === summaryInfoObjectValue) {
        const subcolumns = column.subcolumns.map(
          ({value, displayValue}) =>
            ({
              value: `${column.value}.${value}`,
              displayValue,
              valueType: column.valueType
            }) as IColumnField
        );

        return [...list, ...subcolumns];
      }

      list.push(column);

      return list;
    }, []);
  }

  public setSelectedColumnFromCustomView(customView: ICustomView) {
    const {columns} = customView;

    // Same shape select-columns-view.confirm() emits — the filters step branches
    // on `editTemplate`, so both paths into the store must produce it.
    const selectedColumns = columns.map(column => {
      if (column.value === summaryInfoObjectValue) {
        return {
          field: {
            value: `${column.value}.${column.subcolumn.value}`,
            displayValue: column.subcolumn.displayValue,
            valueType: column.valueType
          },
          operator: '',
          editTemplate: column.valueType
        } as unknown as IColumn;
      } else {
        return {
          field: {...column},
          operator: '',
          editTemplate: column.valueType
        } as unknown as IColumn;
      }
    });

    this.storeSelectedColumns({
      selectedColumns: selectedColumns
    });
  }

  public filterSelectedColumnsByAvailable(view: ICustomView) {
    const availableColumns = this.retrieveRefModelColumns().filter(c => !c.filterOnly);
    view.columns = intersectionBy(view.columns, availableColumns, 'value');
  }

  public setFilterOnlyColumns(columns: IColumnField[]) {
    this.filterOnlyColumns.next(columns);
  }

  public retrieveFilterOnlyColumns() {
    return this.filterOnlyColumns.getValue();
  }
}
